import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// @components
import { Card, Typography, Select } from "@material-tailwind/react";
import {
    Line,
    XAxis,
    Tooltip,
    LineChart,
    CartesianGrid,
    ResponsiveContainer,
} from "recharts";

import { RootState } from '../../../redux';
import {
    ChartMetric,
    ChartPoint,
    listStatsChartRequest,
} from '../../../redux/stats/actions';


function CustomTooltip({ active, payload, label }: any) {
    if (active && payload && payload && payload.length) {
        return (
            <Card className="rounded-lg items-center text-sm pl-2 py-2 pr-3">
                <p className="text-foreground mb-0.5">{label}</p>
                <div className="space-y-1 mt-2">
                    {payload.map(({ name, value, color }: any, key: number) => (
                        <div key={key} className="flex items-center gap-2">
                            <span
                                className="w-2 h-2 rounded-full"
                                style={{
                                    backgroundColor: color,
                                }}
                            />
                            <p className="capitalize">
                                <span className="text-foreground">{name}</span>&nbsp;:&nbsp;
                                <span className="text-black dark:text-white">${value.toLocaleString('en-US')}</span>
                            </p>
                        </div>
                    ))}
                </div>
            </Card>
        );
    }

    return null;
}

type ChartPropsType = ChartMetric & {
    title: string;
}

export function DashboardChart() {
    const dispatch = useDispatch();
    const { chart } = useSelector((state: RootState ) => state.stats);
    const [period, setPeriod] = useState('month');
    const [data, setData] = useState<ChartPropsType[]>([]);
    const [total, setTotal] = useState<ChartPoint>({
        revenue: 0,
        expenses: 0,
        profit: 0,
    });

    useEffect(() => {
        dispatch(listStatsChartRequest({period: period}));
    }, [dispatch, period]);

    useEffect(() => {
        if (!chart)
            return;

        let r = 0;
        let e = 0;
        let p = 0;

        setData(Object.keys(chart?.stats).map((el) => {
            r += chart.stats[el].revenue;
            p += chart.stats[el].profit;
            e += chart.stats[el].expenses;
            return {
                ...chart.stats[el],
                title: el,
            } as unknown as ChartPropsType;
        }));

        setTotal({
            revenue: r,
            expenses: e,
            profit: p,
        });
    }, [chart]);

    return (
        <Card className="h-full flex flex-col justify-between">
            <Card.Header className="w-full justify-between flex items-start gap-4 p-6 m-0">
                <Typography type="h5" color="default">
                    Financial Activity
                </Typography>
                <Select value={period} onValueChange={(val) => setPeriod(val)}>
                    <Select.Trigger className="w-max" placeholder="Select Date" />
                    <Select.List>
                        <Select.Option value="prev_year">Previous Year</Select.Option>
                        <Select.Option value="year">Last Year</Select.Option>
                        <Select.Option value="month">Last Month</Select.Option>
                    </Select.List>
                </Select>
            </Card.Header>
            <Card.Body className="grid lg:grid-cols-4 gap-6 grid-cols-1 p-6">
                <div className="col-span-1 h-full flex flex-col gap-6 justify-between">
                    <div>
                        <Typography
                            type="small"
                            className="text-foreground font-semibold block mb-2"
                        >
                            Expected Revenue
                        </Typography>
                        <Typography as="h2" type="h4" color="success">
                            ${total['revenue'].toLocaleString('en-US')}
                        </Typography>
                    </div>
                    <div>
                        <Typography
                            type="small"
                            className="text-foreground font-semibold block mb-2"
                        >
                            Expenses
                        </Typography>
                        <Typography as="h2" type="h4" color="info">
                            ${total['expenses'].toLocaleString('en-US')}
                        </Typography>
                    </div>
                    <div>
                        <Typography
                            type="small"
                            className="text-foreground font-semibold block mb-2"
                        >
                            Expected Profit
                        </Typography>
                        <Typography as="h2" type="h4" color="primary">
                            ${total['profit'].toLocaleString('en-US')}
                        </Typography>
                    </div>
                </div>
                <div className="col-span-3 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            accessibilityLayer
                            className="[&_svg]:outline-none"
                            data={data}
                        >
                            <CartesianGrid
                                vertical={false}
                                strokeDasharray="3"
                                stroke="rgb(var(--color-surface))"
                            />
                            <Tooltip
                                cursor={false}
                                content={
                                    <CustomTooltip
                                        label={undefined}
                                        active={undefined}
                                        payload={undefined}
                                    />
                                }
                            />
                            <XAxis hide dataKey="title" />
                            <Line
                                dot={false}
                                type="natural"
                                strokeWidth={3}
                                dataKey="revenue"
                                stroke="rgb(var(--color-success))"
                            />
                            <Line
                                dot={false}
                                type="natural"
                                strokeWidth={3}
                                dataKey="expenses"
                                stroke="rgb(var(--color-primary))"
                            />
                            <Line
                                dot={false}
                                type="natural"
                                strokeWidth={3}
                                dataKey="profit"
                                stroke="rgb(var(--color-info))"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card.Body>
        </Card>
    );
}
